/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

/**
 * Monitors scroll position of the host element and emits whether a bottom
 * overflow gradient (scrim) should be visible.
 *
 * The scrim is shown when:
 *  - The directive is enabled.
 *  - The host element's content overflows vertically.
 *  - The user has not scrolled to the bottom.
 *
 * Performance:
 *  - Scroll listener is passive and registered outside Angular zone.
 *  - A ResizeObserver rechecks visibility when the container's dimensions change.
 *  - DOM reads are throttled to one per animation frame via requestAnimationFrame.
 *
 * Usage:
 *   <div [nvScrollScrim]="isEnabled" (nvScrollScrimChange)="showScrim = $event">
 */
@Directive({
    selector: '[nvScrollScrim]',
    standalone: true
})
export class ScrollScrimDirective implements OnInit, OnChanges, OnDestroy {
    /** Enables or disables scroll monitoring. When disabled, emits false. */
    @Input('nvScrollScrim') get enabled() {
        return this._enabled;
    }
    set enabled(value: any) {
        this._enabled = coerceBooleanProperty(value);
    }
    private _enabled = true;

    /** Emits the scrim visibility state whenever it changes. */
    @Output() nvScrollScrimChange = new EventEmitter<boolean>();

    private el: HTMLElement;
    private scrollRAFId: number | null = null;
    private resizeObserver: ResizeObserver | null = null;
    private currentState = false;
    private readonly onScroll = () => this.scheduleCheck();

    constructor(
        ref: ElementRef<HTMLElement>,
        private ngZone: NgZone
    ) {
        this.el = ref.nativeElement;
    }

    ngOnInit(): void {
        this.setupListeners();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['enabled'] && !changes['enabled'].firstChange) {
            this.setupListeners();
        }
    }

    ngOnDestroy(): void {
        this.el.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
        if (this.scrollRAFId !== null) cancelAnimationFrame(this.scrollRAFId);
    }

    /**
     * Adds or removes the scroll listener based on the enabled state.
     * The ResizeObserver is set up once and persists for the directive's lifetime.
     */
    private setupListeners(): void {
        this.el.removeEventListener('scroll', this.onScroll);

        if (this._enabled) {
            this.ngZone.runOutsideAngular(() => {
                this.el.addEventListener('scroll', this.onScroll, { passive: true });
            });
        }

        if (!this.resizeObserver) {
            this.ngZone.runOutsideAngular(() => {
                this.resizeObserver = new ResizeObserver(() => this.scheduleCheck());
                this.resizeObserver.observe(this.el);
            });
        }

        this.scheduleCheck();
    }

    /**
     * Throttles scrim visibility checks to one per animation frame.
     */
    private scheduleCheck(): void {
        if (this.scrollRAFId !== null) cancelAnimationFrame(this.scrollRAFId);
        this.scrollRAFId = requestAnimationFrame(() => {
            this.scrollRAFId = null;
            this.updateVisibility();
        });
    }

    /**
     * Reads the host element's scroll position and emits the scrim state
     * when it changes. Runs inside Angular zone only when the state changes,
     * to trigger change detection in the consuming component.
     */
    private updateVisibility(): void {
        let shouldShow = false;

        if (this._enabled) {
            const { scrollTop, scrollHeight, clientHeight } = this.el;
            const maxScroll = scrollHeight - clientHeight;
            // 2px tolerance accounts for sub-pixel rounding in scroll calculations
            shouldShow = maxScroll > 2 && scrollTop < maxScroll - 2;
        }

        if (this.currentState !== shouldShow) {
            this.currentState = shouldShow;
            this.ngZone.run(() => this.nvScrollScrimChange.emit(shouldShow));
        }
    }
}
